import React, { useState } from 'react';
import {
  FormattedMessage,
  useIntl,
} from 'react-intl';
import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button, Spinner, Modal, TextareaField, Heading, Alert,
} from '@printdeal/ui-library';
import { ErrorManagement } from '@printdeal/error-management/react';
import ExtendQuoteSuccessModal from './ExtendQuoteSuccessModal';
import LineItem from '../../../../../../types/cart/LineItem';
// @ts-expect-error
import { getLocalized } from '../../../../../../helpers/getLocalisedData';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';
import { ExtendQuoteWishes } from '../../../../../../types/quotes/quoteRequest';

interface ExtendQuoteModalProps {
  lineItem: LineItem;
  handleCloseModal: (eventName: string) => void;
}

const ExtendQuoteModal = ({ lineItem, handleCloseModal }: ExtendQuoteModalProps) => {
  const intl = useIntl();
  const { locale } = intl;
  const quotesQuery = ServiceFactory.createQuotesService();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isQuoteExtended, setIsQuoteExtended] = useState(false);

  const {
    quoteId,
    quoteLineItemId,
    localizedProductName,
    productDescription,
  } = lineItem?.custom?.fields || { quoteId: undefined, quoteLineItemId: undefined };

  const extendQuoteFormik = useFormik({
    initialValues: { extendQuoteAdditionalWishes: '' },
    onSubmit: async (values) => {
      setIsLoading(true);
      const quoteLineItemIds: ExtendQuoteWishes[] = [];
      quoteLineItemIds.push({
        id: quoteLineItemId,
        additionalWishes: values.extendQuoteAdditionalWishes,
      });

      try {
        await queryClient.fetchQuery({
          queryKey: ['order', 'extend-quote', lineItem.id],
          queryFn: () => quotesQuery.createQuote({
            quoteLineItems: [],
            quoteId,
            quoteLineItemIds,
          }),
        });
        setIsError(false);
        setIsLoading(false);
        setIsQuoteExtended(true);
        dispatchAccountEvent('order_detail_interaction', 'quote_extention_requested');
      } catch (error) {
        ErrorManagement.exception({
          exception: error,
          tags: {
            fileLocation: 'ExtendQuoteModal',
          },
          fingerprint: ['order-lineItem-extend-quote-submit'],
        });
        setIsError(true);
        setIsLoading(false);
        dispatchAccountEvent('order_list_api_failure', 'quote_extention_request_error');
      }
    },
  });

  const {
    handleSubmit,
    isSubmitting,
    getFieldProps,
    handleChange,
    values,
  } = extendQuoteFormik;

  if (isQuoteExtended) {
    return (
      <ExtendQuoteSuccessModal handleCloseModal={handleCloseModal} />
    );
  }

  return (
    <Modal
      isOpen
      onClose={() => handleCloseModal('modal_extend_quote_close')}
      dismissLabel={intl.formatMessage({
        id: 'account.general.modal.close',
        defaultMessage: 'Close',
      })}
      title={intl.formatMessage({
        id: 'ineItems.list.extendQuote.title',
        defaultMessage: 'Extend Quote',
      })}
    >
      <Modal.Content>
        {isError && (
          <Alert variant="error">
            <FormattedMessage
              id="quotes.created.error"
              defaultMessage="The request could not be processed. Please try again or contact us."
            />
          </Alert>
        )}
        <div className="pd-my-3">
          <Heading size="3">
            <FormattedMessage
              id="lineItems.list.extendQuote.body.title.additionalWishes"
              defaultMessage="Additional wishes if you like to add any"
            />
          </Heading>
        </div>
        <form key="extendQuote-form" id="extendQuote-form" onSubmit={handleSubmit}>
          <p className="pd-mb-3">
            {getLocalized(locale, localizedProductName)}: <span className="pd-text-dim-500">{getLocalized(locale, productDescription)}</span>
          </p>
          <TextareaField
            rows={3}
            className="pd-mb-4 pd-mt-2"
            id="extendQuoteAdditionalWishes"
            {...getFieldProps('extendQuoteAdditionalWishes')}
            onChange={handleChange}
            value={values.extendQuoteAdditionalWishes || ''}
            required
          />
        </form>
      </Modal.Content>
      <Modal.Footer
        className="pd-flex pd-flex-row-reverse pd-flex-wrap pd-gap-2"
      >
        <Button
          variant="primary"
          form="extendQuote-form"
          disabled={Boolean(isLoading || isSubmitting)}
          type="submit"
        >
          {(isLoading || isSubmitting) && (
            <Spinner
              variant="dim"
              size="sm"
              screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            />
          )}
          <FormattedMessage
            id="lineItems.list.extendQuote.submit"
            defaultMessage="Submit"
          />
        </Button>
        <Button variant="secondary" onClick={() => handleCloseModal('modal_extend_quote_cancel')}>
          <FormattedMessage
            id="account.order.service.any.modal.footer.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExtendQuoteModal;
